import { Link } from "react-router-dom";
import ArrowRightIcon from "../../../assets/icons/arrow-right-desktop.svg";
import { useResetPassword } from "../hooks/useResetPassword";
import { PasswordStrength } from "../components/PasswordStrength";
import { EyeToggle } from "../components/EyeToggle";
import styles from "../Auth.module.css";

// Brand wordmark served from /public (PQ VPN Enterprise).
const LOGO_SRC = `${process.env.PUBLIC_URL}/Logo.svg`;

// ── Reset password ─────────────────────────────────────────────────────────────
// Step 2 of the reset flow — the destination of the link emailed by
// forgotPassword: {FRONTEND_URL}/reset-password?token=... (see auth.service.ts).
// Reads the one-time token from the query string, collects a new password, and
// calls POST /auth/reset-password { token, password }. The backend consumes the
// token, updates the password and revokes all sessions; on success the hook sends
// the user back to /login to sign in fresh.
export default function ResetPasswordPage() {
  const {
    token, form, setField, showPassword, togglePassword,
    loading, error, done, submit,
  } = useResetPassword();

  return (
    <div className={styles.page}>
      <div className={styles.bg} />

      <div className={styles.loginCard}>

        {/* ── Logo ── */}
        <div className={styles.loginLogo}>
          <img src={LOGO_SRC} alt="PQ VPN Enterprise" />
        </div>

        {/* ── Dashed divider ── */}
        <div className={styles.loginDivider} />

        {/* ── Title ── */}
        <h2 className={styles.loginTitle}>Set a new password</h2>

        {/* Missing/blank token — the link was malformed or truncated. */}
        {!token ? (
          <>
            <div className={styles.errorBanner}>
              This reset link is missing its token. Please request a new one.
            </div>
            <div className={styles.authLinkRow}>
              <Link to="/forgot" className={styles.loginForgot}>Request a new link</Link>
            </div>
          </>
        ) : done ? (
          <div className={styles.infoBanner}>
            Your password has been reset. Redirecting to sign in…
          </div>
        ) : (
          <>
            {/* ── Error banner — most reset failures are a dead/expired token, so
                offer a direct way to start over. ── */}
            {error && (
              <div className={styles.errorBanner}>
                {error}{" "}
                <Link to="/forgot" className={`${styles.loginForgot} ${styles.inlineLink}`}>Request a new link</Link>
              </div>
            )}

            <form className={styles.loginForm} onSubmit={submit}>
              <div className={styles.loginFormFields}>
                {/* New password */}
                <div className={styles.floatingField}>
                  <div className={styles.loginPassWrap}>
                    <input
                      id="password"
                      className={styles.loginInput}
                      type={showPassword ? "text" : "password"}
                      placeholder=" "
                      value={form.password}
                      onChange={setField("password")}
                      autoComplete="new-password"
                    />
                    <label htmlFor="password" className={styles.floatingLabel}>New password</label>
                    <EyeToggle shown={showPassword} onToggle={togglePassword} />
                  </div>

                  <PasswordStrength password={form.password} />
                </div>

                {/* Confirm password */}
                <div className={styles.floatingField}>
                  <input
                    id="confirm"
                    className={styles.loginInput}
                    type={showPassword ? "text" : "password"}
                    placeholder=" "
                    value={form.confirm}
                    onChange={setField("confirm")}
                    autoComplete="new-password"
                  />
                  <label htmlFor="confirm" className={styles.floatingLabel}>Confirm password</label>
                </div>
              </div>

              {/* Submit button */}
              <button type="submit" className={styles.loginSubmitBtn} disabled={loading}>
                {loading
                  ? <span className={styles.spinner} />
                  : <>
                    <span>Reset password</span>
                    <img src={ArrowRightIcon} alt="" className={styles.loginBtnIcon} />
                  </>}
              </button>
            </form>

            {/* Back to sign in */}
            <div className={styles.authLinkRow}>
              <Link to="/login" className={styles.loginForgot}>Back to sign in</Link>
            </div>
          </>
        )}
      </div>

      <footer className={styles.footer}>
        <span>© 2026 NPQ VPN. All rights reserved.</span>
      </footer>
    </div>
  );
}
